<script setup>
import { reactive, computed, onMounted, onBeforeUnmount, watch, ref, nextTick } from 'vue'

import { useGlobalStore } from '@/stores/useGlobalStore'
import { useCardStore } from '@/stores/useCardStore'
import { useUserStore } from '@/stores/useUserStore'
import { useListStore } from '@/stores/useListStore'
import { useSpaceStore } from '@/stores/useSpaceStore'
import { useBroadcastStore } from '@/stores/useBroadcastStore'

import ColorPicker from '@/components/dialogs/ColorPicker.vue'
import ListActions from '@/components/subsections/ListActions.vue'
import ItemDetailsDebug from '@/components/ItemDetailsDebug.vue'
import ItemTips from '@/components/dialogs/ItemTips.vue'
import { useOnscreenKeyboard } from '@/composables/useOnscreenKeyboard.js'
import utils from '@/utils.js'

import { colord } from 'colord'

const globalStore = useGlobalStore()
const cardStore = useCardStore()
const userStore = useUserStore()
const listStore = useListStore()
const spaceStore = useSpaceStore()
const broadcastStore = useBroadcastStore()

const dialogElement = ref(null)
const nameElement = ref(null)
const { addOnscreenKeyboardListener, removeOnscreenKeyboardListener } = useOnscreenKeyboard(nameElement)

const state = reactive({
  colorPickerIsVisible: false,
  itemTipsIsVisible: false
})

let prevList

const canEditSpace = computed(() => userStore.getUserIsSpaceMember)

const currentList = computed(() => {
  return listStore.getList(globalStore.listDetailsIsVisibleForListId) || {}
})
const visible = computed(() => utils.objectHasKeys(currentList.value))
watch(() => visible.value, async (value, prevValue) => {
  await nextTick()
  if (value) {
    closeDialogs()
    broadcastShowListDetails()
    scrollIntoViewAndFocus()
    textareaSizes()
    globalStore.preventMultipleSelectedActionsIsVisible = false
    prevList = currentList.value
  } else {
    removeOnscreenKeyboardListener()
    globalStore.currentDraggingListId = ''
    globalStore.updateMultipleListsSelectedIds([])
    await nextTick()
    const list = listStore.getList(prevList.id)
    listStore.updateListDimensions(list)
  }
})
const lists = computed(() => listStore.getAllLists)

// dialog state

const closeDialogs = () => {
  state.colorPickerIsVisible = false
  state.itemTipsIsVisible = false
  globalStore.triggerCloseChildDialogs()
}
const closeAllDialogs = () => {
  globalStore.closeAllDialogs()
}
const handleEnterKey = (event) => {
  if (utils.isCompositionKeyboardEvent(event)) { return }
  event.preventDefault()
  closeAllDialogs()
}
const blur = () => {
  globalStore.triggerUpdateHeaderAndFooterPosition()
}
const scrollIntoView = async (behavior) => {
  await nextTick()
  const element = dialogElement.value
  await nextTick()
  await globalStore.scrollElementIntoView({ element, behavior })
}
const scrollIntoViewAndFocus = async () => {
  let behavior
  if (utils.isMobile()) {
    behavior = 'auto'
  }
  await nextTick()
  await scrollIntoView(behavior)
  addOnscreenKeyboardListener()
  focusName()
}

// styles

const styles = computed(() => {
  const position = globalStore.getSpacePositionInPage(currentList.value)
  return {
    left: `${position.x + 8}px`,
    top: `${position.y + 8}px`
  }
})
const broadcastShowListDetails = () => {
  if (!canEditSpace.value) { return }
  const updates = {
    listId: currentList.value.id,
    userId: userStore.id
  }
  broadcastStore.update({ updates, action: 'updateRemoteListDetailsVisible' })
}
const update = (updates) => {
  const keys = Object.keys(updates)
  const update = { id: currentList.value.id }
  keys.forEach(key => {
    update[key] = updates[key]
  })
  listStore.updateList(update)
  // state.isUpdated = true
}
const sectionStyles = computed(() => {
  const backgroundColor = colord(currentList.value.color).alpha(1).toRgbString()
  return { backgroundColor }
})

// name

const name = computed({
  get () {
    return currentList.value.name
  },
  set (name) {
    update({ name })
    textareaSizes()
  }
})
const isDefaultName = computed(() => {
  if (!name.value) { return true }
  const bool = /^List \d+$/.test(name.value)
  return bool
})
const focusName = async () => {
  await nextTick()
  const element = nameElement.value
  const length = name.value.length
  utils.focusTextarea(element)
  selectName()
  globalStore.triggerUpdateHeaderAndFooterPosition()
}
const selectName = () => {
  // select all in new lists, else put cursor at end (like cards)
  const currentListIsNew = globalStore.currentListIsNew
  const element = nameElement.value
  const length = name.value.length
  if (length && element) {
    element.setSelectionRange(0, length)
  }
  globalStore.currentListIsNew = false
}
const textareaSizes = () => {
  const element = dialogElement.value
  const textarea = element.querySelector('textarea')
  let modifier = 0
  if (canEditSpace.value) {
    modifier = 1
  }
  textarea.style.height = textarea.scrollHeight + modifier + 'px'
}
const selectionStartPosition = () => {
  if (!nameElement.value) { return }
  const startPosition = nameElement.value.selectionStart
  return startPosition
}
const selectionEndPosition = () => {
  if (!nameElement.value) { return }
  const endPosition = nameElement.value.selectionEnd
  return endPosition
}
const setSelectionRange = (start, end) => {
  if (nameElement.value) {
    nameElement.value.setSelectionRange(start, end)
  }
}
const toggleTextEditAction = async (action) => {
  if (!visible.value) { return }
  const startPosition = selectionStartPosition()
  const endPosition = selectionEndPosition()
  const { newName, offset } = utils.nameTextEditAction({
    action,
    startPosition,
    endPosition,
    name: name.value
  })
  update({ name: newName })
  textareaSizes()
  await nextTick()
  setSelectionRange(startPosition + offset, endPosition + offset)
}
const textEditLinkAction = async () => {
  if (!visible.value) { return }
  const { newName, newStartPosition, newEndPosition } = utils.nameTextEditLinkAction({
    startPosition: selectionStartPosition(),
    endPosition: selectionEndPosition(),
    name: name.value
  })
  update({ name: newName })
  textareaSizes()
  await nextTick()
  setSelectionRange(newStartPosition, newEndPosition)
}

// colors

const itemColors = computed(() => spaceStore.getSpaceItemColors.list)
const colorisDark = computed(() => {
  const color = currentList.value.color
  return utils.colorIsDark(color)
})
const toggleColorPicker = () => {
  const value = !state.colorPickerIsVisible
  closeDialogs()
  state.colorPickerIsVisible = value
}
const updateColor = (color) => {
  update({ color })
}
const isThemeDarkAndUserColorLight = computed(() => {
  const isThemeDark = userStore.theme === 'dark'
  return isThemeDark && !colorisDark.value
})

// remove

const removeList = () => {
  listStore.removeList(currentList.value.id)
  globalStore.closeAllDialogs()
}

// tips

const toggleItemTipsIsVisible = () => {
  const value = !state.itemTipsIsVisible
  closeDialogs()
  state.itemTipsIsVisible = value
}
</script>

<template lang="pug">
dialog.narrow.link-details(v-if="visible" :open="visible" :style="styles" @click.left.stop="closeDialogs" ref="dialogElement" :date-list-id="currentList.id")
  .dark-theme-background-layer(v-if="isThemeDarkAndUserColorLight")

  section(:style="sectionStyles")
    .row.info-row
      //- color
      .button-wrap
        button.change-color(:disabled="!canEditSpace" @click.left.stop="toggleColorPicker" :class="{active: state.colorPickerIsVisible}")
          .current-color(:style="{backgroundColor: currentList.color}")
        ColorPicker(
          :currentColor="currentList.color"
          :visible="state.colorPickerIsVisible"
          :recentColors="itemColors"
          :luminosityIsDark="true"
          @selectedColor="updateColor"
        )
      //- name
      .textarea-wrap
        textarea.name.on-background-color(
          :disabled="!canEditSpace"
          ref="nameElement"
          rows="1"
          placeholder="List Name"
          v-model="name"
          @keydown.enter.stop="handleEnterKey"
          maxLength="600"
          :class="{'is-dark': colorisDark, 'is-light': !colorisDark}"
          @keydown.meta.b.exact.stop.prevent="toggleTextEditAction('bold')"
          @keydown.ctrl.b.exact.stop.prevent="toggleTextEditAction('bold')"
          @keydown.meta.i.exact.stop.prevent="toggleTextEditAction('italic')"
          @keydown.ctrl.i.exact.stop.prevent="toggleTextEditAction('italic')"
          @keydown.meta.k.exact.stop.prevent="textEditLinkAction"
          @keydown.ctrl.k.exact.stop.prevent="textEditLinkAction"
        )
      //- item tips
      .button-wrap.inline-button-wrap(v-if="isDefaultName" @click.left.stop="toggleItemTipsIsVisible" :class="{ active: state.itemTipsIsVisible }")
        button.small-button(tabindex="-1" :class="{ active: state.itemTipsIsVisible }")
          span ?
        ItemTips(:visible="state.itemTipsIsVisible")
    template(v-if="canEditSpace")
      .row
        //- remove
        .button-wrap(v-if="canEditSpace")
          button.danger(@click.left="removeList" title="Remove List")
            img.icon(src="@/assets/remove.svg")

      ListActions(
        :visible="true"
        :lists="[currentList]"
        @closeDialogs="closeDialogs"
        :colorIsHidden="true"
      )
    .row(v-if="!canEditSpace")
      span.badge.info
        img.icon(src="@/assets/unlock.svg")
        span Read Only

    ItemDetailsDebug(:item="currentList" :keys="['x', 'y', 'height', 'resizeWidth']")
</template>

<style lang="stylus">
dialog.link-details
  transform-origin top left
  .info-row
    align-items flex-start
  .inline-button-wrap
    margin 0
    padding 0
    padding-left 5px
    padding-top 1px
  .up-arrow
    transform rotate(180deg)
  .down-arrow
    padding 0
    vertical-align 3px
</style>
